import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import TvIcon from '@mui/icons-material/Tv'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate } from 'react-router-dom'
import API from '../../services/API'
import { setWallboardToken } from './wallboardToken'
import { wallboardTheme } from './wallboardTheme'

// Authorises a TV: a site admin enters their credentials once (plus their
// authenticator code if they have one), the server mints a device token, and
// the admin walks away WITHOUT being signed in on this device — no session
// cookie is ever issued by the unlock endpoint.
const WallboardUnlock = ({ onUnlocked }) => {
  const navigate = useNavigate()
  const [deviceName, setDeviceName] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [mfaRequired, setMfaRequired] = useState(false)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async (event) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const res = await API.post(
        '/wallboard/unlock',
        {
          username: username.trim(),
          password,
          deviceName: deviceName.trim(),
          ...(totp ? { totp: totp.trim() } : {}),
        },
        { skipGlobalErrorHandler: true },
      )

      if (res.status === 200 && res.data?.token) {
        setWallboardToken(res.data.token)
        onUnlocked()
        return
      }
      if (res.status === 401 && res.data?.mfaRequired) {
        setMfaRequired(true)
        return
      }
      if (res.status === 429) {
        setError('Too many attempts — wait a few minutes and try again.')
        return
      }
      if (res.data?.errorCode === 'INSUFFICIENT_SUBSCRIPTION_TIER') {
        setError('Wallboards are part of the Premium plan.')
        return
      }
      setError(res.data?.error ?? 'Unable to unlock the wallboard.')
    } catch {
      setError('Unable to reach AmbuNet — check the connection and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ThemeProvider theme={wallboardTheme}>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
          zIndex: 1300,
        }}
      >
        <Paper
          component="form"
          onSubmit={submit}
          variant="outlined"
          sx={{ p: 4, width: '100%', maxWidth: 430 }}
        >
          <Stack gap={2.5}>
            <Stack direction="row" gap={1.5} alignItems="center">
              <TvIcon color="primary" sx={{ fontSize: 34 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
                  Set up this wallboard
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  A site admin authorises the display once. Authorising does{' '}
                  <strong>not</strong> sign you in on this device.
                </Typography>
              </Box>
            </Stack>

            <TextField
              label="Display name"
              placeholder="e.g. Crew room TV"
              value={deviceName}
              onChange={(event) => setDeviceName(event.target.value)}
              inputProps={{ maxLength: 60 }}
              fullWidth
            />
            <TextField
              label="Admin email"
              type="email"
              autoComplete="off"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="off"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              fullWidth
            />
            {mfaRequired && (
              <TextField
                label="Authenticator code"
                value={totp}
                onChange={(event) =>
                  setTotp(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                autoFocus
                required
                fullWidth
              />
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || (mfaRequired && totp.length < 6)}
            >
              {submitting ? 'Checking…' : 'Unlock wallboard'}
            </Button>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ color: 'text.secondary', textTransform: 'none' }}
            >
              Back to sign in
            </Button>
          </Stack>
        </Paper>
      </Box>
    </ThemeProvider>
  )
}

export default WallboardUnlock
